import { useEffect, useRef } from 'react';
import { useAuth } from '../lib/auth-context';
import {
  CHECKINS_UPDATED_EVENT,
  CLOUD_BOOTSTRAP_DONE_KEY,
  ensureRecoveredAccountState,
  ensureSyncEnabledByDefault,
  startPeriodicSync,
  syncNow,
} from '../lib/cloud-sync';

/**
 * Keeps cloud sync automatic for signed-in users.
 * - Initializes "sync on" defaults once.
 * - Runs startup and periodic background sync.
 * - Syncs immediately after local check-ins are saved.
 */
export function CloudSyncAutoManager() {
  const { user, accessToken } = useAuth();
  const checkinSyncTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!user || !accessToken || typeof window === 'undefined') return;

    ensureSyncEnabledByDefault();

    // Run first sync immediately so Splash / Index routing see merged cloud + localStorage state.
    const startupTimer = window.setTimeout(() => {
      void (async () => {
        try {
          await syncNow(accessToken, { force: true });
        } finally {
          // Always normalize local account state and mark bootstrap done,
          // even if cloud sync fails (offline, auth, etc.).
          ensureRecoveredAccountState();
          localStorage.setItem(CLOUD_BOOTSTRAP_DONE_KEY, 'true');
        }
      })();
    }, 0);

    const stopPeriodicSync = startPeriodicSync(accessToken);
    return () => {
      window.clearTimeout(startupTimer);
      stopPeriodicSync();
    };
  }, [user, accessToken]);

  useEffect(() => {
    if (!user || !accessToken || typeof window === 'undefined') return;

    const handleCheckInSaved = () => {
      if (checkinSyncTimerRef.current != null) {
        window.clearTimeout(checkinSyncTimerRef.current);
      }
      // Coalesce rapid check-in writes and let cloud sync run once.
      checkinSyncTimerRef.current = window.setTimeout(() => {
        checkinSyncTimerRef.current = null;
        void syncNow(accessToken, { force: false });
      }, 6000);
    };

    window.addEventListener(CHECKINS_UPDATED_EVENT, handleCheckInSaved);
    return () => {
      if (checkinSyncTimerRef.current != null) {
        window.clearTimeout(checkinSyncTimerRef.current);
        checkinSyncTimerRef.current = null;
      }
      window.removeEventListener(CHECKINS_UPDATED_EVENT, handleCheckInSaved);
    };
  }, [user, accessToken]);

  return null;
}
